import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface AreaDocLayoutProps {
  icon: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  title: string;
  subtitle: string;
  tag?: string;
  tagColor?: string;
  children: React.ReactNode;
}

export function AreaDocLayout({
  icon: Icon,
  iconBg = 'bg-primary-50 border-primary-200',
  iconColor = 'text-primary-600',
  title,
  subtitle,
  tag,
  tagColor = 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  children,
}: AreaDocLayoutProps) {
  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <div>
        <Link
          to="/prioritization/documentation"
          className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-primary-600 transition-colors mb-4"
        >
          <ChevronLeft className="h-3 w-3" />
          Documentation
        </Link>

        <div className="flex items-start gap-3">
          <div className={`w-9 h-9  border flex items-center justify-center flex-shrink-0 ${iconBg}`}>
            <Icon className={`h-4 w-4 ${iconColor}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[var(--text-pri)] font-heading">{title}</h1>
              {tag && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tagColor}`}>{tag}</span>}
            </div>
            <p className="text-sm text-[var(--text-ter)] mt-0.5">{subtitle}</p>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

export function DocDivider() {
  return <hr className="border-[var(--rule)]" />;
}

export function ReferenceTable({ rows }: { rows: { field: string; type: string; description: string }[] }) {
  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-40">Field</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-28">Type</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.field} className={i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}>
              <td className="px-4 py-2.5 font-mono text-xs text-[var(--text-ter)]">{row.field}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-[var(--text-ter)]">{row.type}</td>
              <td className="px-4 py-2.5 text-xs text-[var(--text-sec)]">{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
